import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  CalendarIcon,
  DocumentTextIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import subscriptionAPI from '../services/subscriptionAPI';
import LoadingScreen from '../components/common/LoadingScreen';

const SubscriptionSuccess = () => {
  const { subscriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        setLoading(true);
        
        // Get payment ID from URL parameters
        const paymentId = searchParams.get('id') || searchParams.get('payment_id');
        
        if (!paymentId) {
          setError('معرف الدفع مفقود');
          return;
        }

        console.log('Verifying subscription:', { subscriptionId, paymentId });
        
        setVerifying(true);
        const response = await subscriptionAPI.verifySubscription(subscriptionId, paymentId);
        
        if (response.success) {
          setSubscription(response.subscription);
          setVerified(true);
        } else {
          setError('فشل في تفعيل الاشتراك');
        }
      } catch (error) {
        console.error('Subscription verification error:', error);
        setError(error || 'حدث خطأ أثناء تفعيل الاشتراك');
      } finally {
        setLoading(false);
        setVerifying(false);
      }
    };

    if (subscriptionId) {
      verifySubscription();
    }
  }, [subscriptionId, searchParams]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">فشل في تفعيل الاشتراك</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => navigate('/subscription-plans')}
            className="w-full px-6 py-3 bg-brand-blue text-white rounded-xl font-medium hover:bg-brand-blue/90 transition-colors"
          >
            العودة إلى صفحة الاشتراكات
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-green/10 to-brand-blue/10 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-blue to-brand-green p-8 text-center text-white">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <CheckCircleIcon className="w-12 h-12" />
          </motion.div>
          <h1 className="text-3xl font-bold mb-2">مبروك! 🎉</h1>
          <p className="text-lg opacity-90">تم تفعيل اشتراك بديل بلس بنجاح</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {verifying ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4"></div>
              <p className="text-gray-600">جاري تفعيل الاشتراك...</p>
            </div>
          ) : verified && subscription ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {/* Subscription Details */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">تفاصيل الاشتراك</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-brand-blue" />
                    <div>
                      <p className="text-sm text-gray-600">تاريخ البداية</p>
                      <p className="font-medium">{formatDate(subscription.startDate)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-brand-green" />
                    <div>
                      <p className="text-sm text-gray-600">تاريخ الانتهاء</p>
                      <p className="font-medium">{formatDate(subscription.endDate)}</p>
                    </div>
                  </div>
                  
                  {subscription.invoiceNumber && (
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">رقم الفاتورة</p>
                        <p className="font-medium">{subscription.invoiceNumber}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-r from-brand-blue/5 to-brand-green/5 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">مميزاتك الجديدة</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-brand-green" />
                    <span className="text-gray-700">إعفاء كامل من عمولة المنصة 18%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-brand-green" />
                    <span className="text-gray-700">10 عمليات تبادل مجانية سنوياً</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-brand-green" />
                    <span className="text-gray-700">دعم فني مميز وأولوية</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/lab/dashboard')}
                  className="w-full px-6 py-3 bg-brand-blue text-white rounded-xl font-medium hover:bg-brand-blue/90 transition-colors flex items-center justify-center gap-2"
                >
                  <span>انتقل إلى لوحة التحكم</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => navigate('/products')}
                  className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  تصفح المنتجات
                </button>
              </div>
            </motion.div>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
